// The opening titles: "Chat Brawlers", subtitled "Chaos to Consensus", over the
// logo's three bodies in Lagrange's orbit. After a second or so the title recedes
// into the distance, Star Wars style, and the scene parts like theatre curtains
// onto the app. A click, Escape, Enter or Space skips to the curtains.
//
// playIntro(root, { reducedMotion }) resolves once the intro is gone.

// About 3.5 s from first frame to the app: title 2 s, recede 0.7 s, curtains 0.8 s.
// The CSS durations in style.css (arrive, subtitle, recede, .curtain) match these.
const HOLD_MS = 2000; // the title on screen before it recedes
const RECEDE_MS = 700;
const CURTAIN_MS = 800;

export function playIntro(root, { reducedMotion = false } = {}) {
  const canvas = root.querySelector(".intro-sky");
  const title = root.querySelector(".intro-titles");
  const skip = root.querySelector(".intro-skip");
  const sky = drawOrbits(canvas, { reducedMotion });
  root.hidden = false;
  root.dataset.phase = "title";

  return new Promise((resolve) => {
    const timers = [];
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      for (const t of timers) clearTimeout(t);
      removeEventListener("keydown", onKey);
      sky.stop();
      root.remove();
      resolve();
    };

    // The curtains are two halves of a still of the sky (title gone) sliding apart.
    const part = () => {
      if (root.dataset.phase === "curtains") return;
      root.dataset.phase = "curtains";
      if (reducedMotion) {
        root.classList.add("fade");
        timers.push(setTimeout(finish, 400));
        return;
      }
      const still = sky.still();
      sky.stop();
      canvas.remove();
      title.remove();
      for (const side of ["left", "right"]) {
        const half = document.createElement("div");
        half.className = `curtain ${side}`;
        if (still) half.style.backgroundImage = `url(${still})`;
        root.append(half);
      }
      // Two frames, so the halves are laid out closed before they open.
      requestAnimationFrame(() => requestAnimationFrame(() => root.classList.add("open")));
      timers.push(setTimeout(finish, CURTAIN_MS + 100));
    };

    const recede = () => {
      if (root.dataset.phase !== "title") return;
      root.dataset.phase = "recede";
      if (reducedMotion) return part();
      title.classList.add("recede");
      timers.push(setTimeout(part, RECEDE_MS));
    };

    const onKey = (e) => {
      if (e.key === "Escape" || e.key === "Enter" || e.key === " ") part();
    };
    skip.addEventListener("click", part);
    root.addEventListener("click", (e) => e.target !== skip && part());
    addEventListener("keydown", onKey);
    skip.focus({ preventScroll: true });
    timers.push(setTimeout(recede, reducedMotion ? 1000 : HOLD_MS));
  });
}

// Lagrange's solution of the three-body problem, the logo come to life: three
// equal bodies on Kepler ellipses sharing a focus, 120 degrees apart, always at
// the corners of an equilateral triangle, each trailing the last stretch of its
// orbit. Returns { still(), stop() }.
const SEATS = [[240, 147, 109], [61, 219, 176], [141, 157, 255]]; // Anthropic, OpenAI, open weights
const ECC = 0.62;
const PERIOD_S = 6;
const TAIL = 3; // the trail, in radians of mean anomaly (about half an orbit)
const POSE = 1.7; // where the logo shows them, and the still for reduced motion

// The true anomaly at mean anomaly m (Kepler's equation, by Newton's method).
function trueAnomaly(m) {
  let e = m;
  for (let i = 0; i < 8; i++) e -= (e - ECC * Math.sin(e) - m) / (1 - ECC * Math.cos(e));
  return 2 * Math.atan2(Math.sqrt(1 + ECC) * Math.sin(e / 2), Math.sqrt(1 - ECC) * Math.cos(e / 2));
}

function drawOrbits(canvas, { reducedMotion }) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return { still: () => null, stop() {} };
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  let w = 0;
  let h = 0;
  let apo = 0; // the farthest a body gets from the centre
  let raf = 0;
  let stopped = false;
  let m = POSE - 2.4; // the bodies' mean anomaly; they arrive at the logo's pose as the title settles

  let seed = 7;
  const rand = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  const stars = Array.from({ length: 420 }, () => ({ x: rand(), y: rand(), b: 0.25 + rand() * 0.75, s: rand() < 0.08 ? 1.6 : 0.9, tw: rand() * 6.3 }));

  const cx = () => w / 2;
  const cy = () => h * 0.38;
  // Body k at mean anomaly m: its ellipse's apocentre points up for k = 0.
  function at(k, m) {
    const nu = trueAnomaly(m);
    const r = (apo / (1 + ECC)) * (1 - ECC * ECC) / (1 + ECC * Math.cos(nu));
    const a = nu - Math.PI / 2 + (k * 2 * Math.PI) / 3;
    return [cx() + r * Math.cos(a), cy() - r * Math.sin(a)];
  }

  let orbits = [];
  function resize() {
    w = canvas.clientWidth || innerWidth;
    h = canvas.clientHeight || innerHeight;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    apo = Math.max(60, Math.min(w * 0.3, h * 0.22)); // clear of the title below
    orbits = SEATS.map((_, k) => {
      const path = new Path2D();
      for (let i = 0; i <= 180; i++) path[i ? "lineTo" : "moveTo"](...at(k, (i / 180) * Math.PI * 2));
      return path;
    });
    if (reducedMotion) frame(0); // no loop to redraw it
  }

  function drawStars(t) {
    for (const s of stars) {
      ctx.fillStyle = `rgba(220,228,255,${s.b * (0.65 + 0.15 * Math.sin(t * 1.3 + s.tw))})`;
      ctx.fillRect(s.x * w, s.y * h, s.s, s.s);
    }
  }

  function drawBodies() {
    const lw = Math.max(1.5, apo * 0.012);
    ctx.lineCap = "butt"; // round caps would overlap into beads along the trail
    ctx.globalCompositeOperation = "lighter"; // where trails cross they glow brighter
    SEATS.forEach(([r, g, b], k) => {
      ctx.strokeStyle = `rgba(${r},${g},${b},0.22)`;
      ctx.lineWidth = lw;
      ctx.stroke(orbits[k]);
      // The trail, brightening and thickening toward the body; a wide faint pass under it glows.
      const N = 48;
      for (let s = 0; s < N; s++) {
        const f = (s + 1) / N;
        const [x0, y0] = at(k, m - TAIL * (1 - s / N));
        const [x1, y1] = at(k, m - TAIL * (1 - f));
        ctx.beginPath();
        ctx.moveTo(x0, y0);
        ctx.lineTo(x1, y1);
        ctx.strokeStyle = `rgba(${r},${g},${b},${0.12 * f})`;
        ctx.lineWidth = lw * 7 * f;
        ctx.stroke();
        ctx.strokeStyle = `rgba(${r},${g},${b},${0.15 + 0.85 * f})`;
        ctx.lineWidth = lw * (0.6 + 1.6 * f);
        ctx.stroke();
      }
    });
    ctx.globalCompositeOperation = "source-over";
    SEATS.forEach(([r, g, b], k) => {
      const [x, y] = at(k, m);
      const size = Math.max(5, apo * 0.045);
      const glow = ctx.createRadialGradient(x, y, 0, x, y, size * 4);
      glow.addColorStop(0, `rgba(${r},${g},${b},0.55)`);
      glow.addColorStop(1, `rgba(${r},${g},${b},0)`);
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(x, y, size * 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#fff3e2";
      ctx.beginPath();
      ctx.arc(x, y, size, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = `rgb(${r},${g},${b})`;
      ctx.beginPath();
      ctx.arc(x, y, size * 0.72, 0, Math.PI * 2);
      ctx.fill();
    });
  }

  let clock = 0;
  function frame(dt) {
    clock += dt;
    m += (dt / PERIOD_S) * Math.PI * 2;
    if (reducedMotion) m = POSE;
    ctx.fillStyle = "#020308";
    ctx.fillRect(0, 0, w, h);
    drawStars(clock);
    drawBodies();
  }

  let last = 0;
  function loop(t) {
    if (stopped) return;
    const dt = last ? Math.min(0.05, (t - last) / 1000) : 0.016;
    last = t;
    frame(dt);
    canvas.dataset.frames = String(Number(canvas.dataset.frames ?? 0) + 1);
    raf = requestAnimationFrame(loop);
  }

  resize();
  addEventListener("resize", resize);
  if (!reducedMotion) raf = requestAnimationFrame(loop);

  return {
    still() {
      try {
        return canvas.toDataURL("image/jpeg", 0.85);
      } catch {
        return null;
      }
    },
    stop() {
      stopped = true;
      cancelAnimationFrame(raf);
      removeEventListener("resize", resize);
    },
  };
}
